import React from "react";
import Avatar from "@/components/Avatar";
import { UserPlus, UserMinus } from "lucide-react";

/** Members tab — list current channel members and invite from the org. */
export default function MembersTab({
  isPublic, members, addable, canManage, currentUserId,
  createdBy, onAdd, onRemove,
}) {
  return (
    <div className="space-y-3 pt-1">
      {isPublic && (
        <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
          This is a public channel. All org members ({members.length}) are automatically included.
          Convert to a private channel in the Info tab to invite specific people.
        </div>
      )}
      <div className="border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
          <span>Current members</span>
          <span className="tabular-nums text-zinc-600">{members.length}</span>
        </div>
        <div className="max-h-52 overflow-y-auto scroll-thin">
          {members.map((m) => {
            const isCreator = m.user_id === createdBy;
            const isSelf = m.user_id === currentUserId;
            const canRemove = !isPublic && !isCreator && (canManage || isSelf);
            return (
              <div
                key={m.user_id}
                className="flex items-center gap-2.5 px-3 py-2 hover:bg-zinc-50"
                data-testid={`channel-member-${m.user_id}`}
              >
                <Avatar user={m.user} size="sm"/>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-zinc-900 truncate">
                    {m.user?.name || m.user?.email}
                    {isCreator && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-[var(--brand-accent)] font-semibold">Creator</span>}
                    {isSelf && !isCreator && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-zinc-400 font-semibold">You</span>}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">{m.user?.title || m.user?.email}</div>
                </div>
                {canRemove && (
                  <button
                    onClick={() => onRemove(m.user_id)}
                    className="p-1.5 rounded hover:bg-red-50 text-zinc-500 hover:text-red-600"
                    data-testid={`channel-member-remove-${m.user_id}`}
                    title={isSelf ? "Leave channel" : "Remove from channel"}
                  >
                    <UserMinus className="w-3.5 h-3.5"/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {!isPublic && canManage && addable.length > 0 && (
        <div className="border border-zinc-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold border-b border-zinc-100 bg-zinc-50">
            Invite from org
          </div>
          <div className="max-h-40 overflow-y-auto scroll-thin">
            {addable.map((m) => (
              <div
                key={m.user_id}
                className="flex items-center gap-2.5 px-3 py-2 hover:bg-zinc-50"
              >
                <Avatar user={m.user} size="sm"/>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-zinc-900 truncate">{m.user?.name || m.user?.email}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{m.department || m.role || m.user?.email}</div>
                </div>
                <button
                  onClick={() => onAdd(m.user_id)}
                  className="p-1.5 rounded hover:bg-emerald-50 text-zinc-500 hover:text-emerald-700"
                  data-testid={`channel-member-add-${m.user_id}`}
                  title="Add to channel"
                >
                  <UserPlus className="w-3.5 h-3.5"/>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
